import { useMemo, useSyncExternalStore } from "react";

export interface ToolCallEntry {
  toolCallId: string;
}

export type ChatCardStage = "awaiting" | "done";

export interface ToolCallStore<E extends ToolCallEntry> {
  open(entry: E): void;
  answered(toolCallId: string): void;
  answering(toolCallId: string): boolean;
  has(toolCallId: string): boolean;
  read(toolCallId: string): E | null;
  all(): E[];
  update(toolCallId: string, change: (entry: E) => E): void;
  useEntry(toolCallId: string): E | null;
  useAll(): E[];
  useAnswering(toolCallId: string): boolean;
  useStages(): (toolCallId: string) => ChatCardStage | null;
}

export interface ToolCallStoreSpec<E extends ToolCallEntry> {
  key: string;
  narrow: (value: unknown) => E | null;
  stage: (entry: E) => ChatCardStage;
}

export function toolCallStore<E extends ToolCallEntry>({
  key,
  narrow,
  stage,
}: ToolCallStoreSpec<E>): ToolCallStore<E> {
  const answeringNow = new Set<string>();
  const listeners = new Set<() => void>();
  const held: { raw: string | null; stored: boolean } = { raw: null, stored: true };

  function open(entry: E): void {
    answeringNow.add(entry.toolCallId);
    const kept = readAll().filter((known) => known.toolCallId !== entry.toolCallId);
    write([...kept, entry]);
  }

  function answered(toolCallId: string): void {
    if (answeringNow.delete(toolCallId)) notify();
  }

  function answering(toolCallId: string): boolean {
    return answeringNow.has(toolCallId);
  }

  function has(toolCallId: string): boolean {
    return readAll().some((entry) => entry.toolCallId === toolCallId);
  }

  function read(toolCallId: string): E | null {
    return readAll().find((entry) => entry.toolCallId === toolCallId) ?? null;
  }

  function update(toolCallId: string, change: (entry: E) => E): void {
    const entries = readAll();
    if (!entries.some((entry) => entry.toolCallId === toolCallId)) return;
    write(entries.map((entry) => (entry.toolCallId === toolCallId ? change(entry) : entry)));
  }

  function useEntry(toolCallId: string): E | null {
    const raw = useSyncExternalStore(subscribe, rawSnapshot);
    return useMemo(
      () => entriesOf(raw).find((entry) => entry.toolCallId === toolCallId) ?? null,
      [raw, toolCallId],
    );
  }

  function useAll(): E[] {
    const raw = useSyncExternalStore(subscribe, rawSnapshot);
    return useMemo(() => entriesOf(raw), [raw]);
  }

  function useAnswering(toolCallId: string): boolean {
    return useSyncExternalStore(subscribe, () => answeringNow.has(toolCallId));
  }

  function useStages(): (toolCallId: string) => ChatCardStage | null {
    const raw = useSyncExternalStore(subscribe, rawSnapshot);
    const answeringIds = useSyncExternalStore(subscribe, answeringSnapshot);
    return useMemo(() => {
      const entries = entriesOf(raw);
      const holding = new Set(answeringIds.split("\n"));
      return (toolCallId) => {
        const entry = entries.find((candidate) => candidate.toolCallId === toolCallId);
        if (entry === undefined || holding.has(toolCallId)) return null;
        return stage(entry);
      };
    }, [raw, answeringIds]);
  }

  function readAll(): E[] {
    return entriesOf(rawSnapshot());
  }

  function write(entries: E[]): void {
    held.raw = JSON.stringify(entries.slice(-MAX_ENTRIES));
    try {
      window.sessionStorage.setItem(key, held.raw);
    } catch {
      held.stored = false;
    }
    notify();
  }

  function rawSnapshot(): string | null {
    if (!held.stored) return held.raw;
    try {
      return window.sessionStorage.getItem(key);
    } catch {
      return held.raw;
    }
  }

  function answeringSnapshot(): string {
    return [...answeringNow].join("\n");
  }

  function subscribe(listener: () => void): () => void {
    listeners.add(listener);
    return () => listeners.delete(listener);
  }

  function notify(): void {
    for (const listener of listeners) listener();
  }

  function entriesOf(raw: string | null): E[] {
    if (!raw) return [];
    let parsed: unknown;
    try {
      parsed = JSON.parse(raw);
    } catch {
      return [];
    }
    if (!Array.isArray(parsed)) return [];
    return parsed.flatMap((value) => {
      const entry = narrow(value);
      return entry === null || entry.toolCallId === "" ? [] : [entry];
    });
  }

  return {
    open,
    answered,
    answering,
    has,
    read,
    all: readAll,
    update,
    useEntry,
    useAll,
    useAnswering,
    useStages,
  };
}

const MAX_ENTRIES = 12;
